// src/pages/preventivas/PreventivasFinalizadasPage.jsx
import React, { useEffect, useMemo, useState } from "react";

import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useIsMobile } from "../../hooks/useIsMobile";
import LayoutDesktop from "../../layouts/LayoutDesktop";
import LayoutMobile from "../../layouts/LayoutMobile";
import CardListMobile from "../../templates/mobile/CardListMobile";
import { IconHash, IconMachine, IconCalendar } from "../../components/Icons/Icons";

import { db } from "../../config/firebase";
import { collection, deleteDoc, doc, getDocs, query, where } from "firebase/firestore";

import { formatData, formatDataHora, timestampToDate } from "../../utils/dateFormat";
import { montarNomeExibicaoPreventiva } from "../../utils/preventivaNome";

function PreventivasFinalizadasPage() {
  const { tema, alterarTema } = useTheme();
  const { usuario: usuarioLogado } = useAuth();
  const isMobile = useIsMobile(900);

  const [sidebarAberta, setSidebarAberta] = useState(false);
  const [execucoes, setExecucoes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  const [textoFiltro, setTextoFiltro] = useState("");
  const [execucaoSelecionada, setExecucaoSelecionada] = useState(null);
  const [deletandoId, setDeletandoId] = useState(null);

  useEffect(() => {
    async function carregarExecucoes() {
      if (!usuarioLogado?.grupoId) return;

      setCarregando(true);
      setErro("");

      try {
        const ref = collection(db, "preventivasExecucoes");
        // Evita índice composto (where + orderBy) no Firestore.
        // Ordenamos no cliente para não quebrar em ambientes de teste/primeiro uso.
        const q = query(ref, where("grupoId", "==", usuarioLogado.grupoId));
        const snap = await getDocs(q);

        const lista = snap.docs
          .map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
          }))
          .sort((a, b) => {
            const da = timestampToDate(a.dataExecucao)?.getTime?.() ?? 0;
            const dbb = timestampToDate(b.dataExecucao)?.getTime?.() ?? 0;
            return dbb - da;
          });

        setExecucoes(lista);
      } catch (e) {
        console.error("Erro ao carregar execuções de preventivas:", e);
        setErro("Erro ao carregar preventivas finalizadas. Tente novamente.");
      } finally {
        setCarregando(false);
      }
    }

    carregarExecucoes();
  }, [usuarioLogado]);

  const execucoesFiltradas = useMemo(() => {
    const texto = textoFiltro.trim().toLowerCase();
    if (!texto) return execucoes;

    return execucoes.filter((e) => {
      const campos = [
        e.preventivaCodigo,
        e.preventivaNome,
        montarNomeExibicaoPreventiva(e),
        e.empresaNome,
        e.maquinaNome,
        e.executanteNome,
      ];
      return campos.some((campo) =>
        String(campo || "").toLowerCase().includes(texto)
      );
    });
  }, [execucoes, textoFiltro]);

  const execucaoPorId = useMemo(() => {
    const m = new Map();
    execucoesFiltradas.forEach((e) => m.set(e.id, e));
    return m;
  }, [execucoesFiltradas]);

  const itemsParaCards = execucoesFiltradas.map((e) => ({
    id: e.id,
    nome: montarNomeExibicaoPreventiva(e),
    subtitulo: e.empresaNome || "-",
    detalhes: [
      {
        icone: <IconHash size="14px" />,
        label: "Código",
        valor: e.preventivaCodigo || "-",
      },
      {
        icone: <IconMachine size="14px" />,
        label: "Máquina",
        valor: e.maquinaNome || "-",
      },
      {
        icone: <IconCalendar size="14px" />,
        label: "Execução",
        valor: formatDataHora(e.dataExecucao),
      },
      {
        icone: <IconCalendar size="14px" />,
        label: "Periodicidade",
        valor: e.periodicidade || "-",
      },
      {
        icone: <IconCalendar size="14px" />,
        label: "Executante",
        valor: e.executanteNome || "-",
      },
    ],
  }));


  function abrirDetalhes(execucao) {
    setExecucaoSelecionada(execucao);
  }

  function fecharDetalhes() {
    setExecucaoSelecionada(null);
  }

  async function handleDeletar(execucaoId) {
    if (!execucaoId) return;
    
    const confirmar = window.confirm(
      "Tem certeza que deseja excluir esta execução de preventiva? Esta ação não pode ser desfeita."
    );
    if (!confirmar) return;

    setDeletandoId(execucaoId);
    try {
      await deleteDoc(doc(db, "preventivasExecucoes", execucaoId));
      setExecucoes((prev) => prev.filter((e) => e.id !== execucaoId));
      
      // Se o modal de detalhes estava aberto para este item, fecha
      if (execucaoSelecionada?.id === execucaoId) {
        setExecucaoSelecionada(null);
      }
    } catch (e) {
      console.error("Erro ao excluir execução:", e);
      setErro("Erro ao excluir execução. Verifique suas permissões.");
    } finally {
      setDeletandoId(null);
    }
  }

  if (isMobile) {
    return (
      <LayoutMobile
        tema={tema}
        alterarTema={alterarTema}
        titulo="Preventivas Finalizadas"
        sidebarAberta={sidebarAberta}
        onToggleSidebar={() => setSidebarAberta(!sidebarAberta)}
      >
        <div style={{ padding: "8px 12px" }}>
          <input
            type="text"
            value={textoFiltro}
            onChange={(e) => setTextoFiltro(e.target.value)}
            placeholder="Filtrar por código, nome, máquina..."
            style={{
              width: "100%",
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #334155",
              
              color: "inherit",
              marginBottom: 8,
            }}
          />
        </div>


      {execucaoSelecionada && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={fecharDetalhes}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1000,
          }}
        >
          <div
            className="pc-card"
            onClick={(ev) => ev.stopPropagation()}
            style={{
              width: "min(980px, 96vw)",
              maxHeight: "86vh",
              overflow: "auto",
            }}
          >
            <header
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>Respostas da preventiva</h3>
                <p style={{ marginTop: 6, opacity: 0.85 }}>
                  {execucaoSelecionada.preventivaCodigo
                    ? `Código: ${execucaoSelecionada.preventivaCodigo} · `
                    : ""}
                  {montarNomeExibicaoPreventiva(execucaoSelecionada) || ""}
                </p>
                <p style={{ marginTop: 6, opacity: 0.85 }}>
                  {execucaoSelecionada.empresaNome
                    ? `Empresa: ${execucaoSelecionada.empresaNome} · `
                    : ""}
                  {execucaoSelecionada.maquinaNome
                    ? `Máquina: ${execucaoSelecionada.maquinaNome} · `
                    : ""}
                  Execução: {formatDataHora(execucaoSelecionada.dataExecucao)}
                </p>
              </div>

              <button
                type="button"
                className="pc-btn-secundario"
                onClick={fecharDetalhes}
                style={{ padding: "8px 12px" }}
              >
                Fechar
              </button>
            </header>

            <div style={{ marginTop: 14 }}>
              <h4 style={{ marginTop: 0 }}>Checklist / Perguntas e respostas</h4>

              {Array.isArray(execucaoSelecionada.respostas) &&
              execucaoSelecionada.respostas.length > 0 ? (
                <div className="pc-table-wrapper" style={{ marginTop: 10 }}>
                  <table className="pc-table">
                    <thead>
                      <tr>
                        <th style={{ width: 80 }}>#</th>
                        <th>Pergunta</th>
                        <th style={{ width: 240 }}>Resposta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {execucaoSelecionada.respostas.map((r, i) => (
                        <tr key={String(r?.index ?? i)}>
                          <td>{(r?.index ?? i) + 1}</td>
                          <td>{r?.pergunta || "-"}</td>
                          <td>{r?.valor ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ opacity: 0.85 }}>
                  Nenhuma resposta registrada nesta execução.
                </p>
              )}

              {execucaoSelecionada.observacoes && (
                <div style={{ marginTop: 14 }}>
                  <h4 style={{ marginBottom: 8 }}>Observações</h4>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      padding: 12,
                      borderRadius: 10,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {execucaoSelecionada.observacoes}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

        <CardListMobile
          items={itemsParaCards}
          renderCard={(item) => {
            const exec = execucaoPorId.get(item.id);
            return (
              <article
                key={item.id}
                className="m-user-card"
                style={{ cursor: "pointer" }}
                onClick={() => exec && abrirDetalhes(exec)}
              >
                <div className="m-user-card-topbar" />
                <div className="m-card-content">
                  <div className="m-card-header">
                    <div className="m-card-avatar">PR</div>
                    <div className="m-card-title">
                      <h4 className="m-card-name">{item.nome}</h4>
                      {item.subtitulo && (
                        <span className="m-card-badge badge-tecnico">{item.subtitulo}</span>
                      )}
                    </div>
                  </div>

                  {item.detalhes && item.detalhes.length > 0 && (
                    <div className="m-card-details">
                      {item.detalhes.map((detalhe, idx) => (
                        <div key={idx} className="m-card-detail-row">
                          <span className="m-card-detail-icon">{detalhe.icone}</span>
                          <span className="m-card-detail-label">{detalhe.label}</span>
                          <span className="m-card-detail-value">{detalhe.valor || "-"}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  <div style={{ marginTop: 10, display: "flex", gap: 8, justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      className="m-btn-pill m-btn-secondary"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        exec && abrirDetalhes(exec);
                      }}
                    >
                      Ver respostas
                    </button>
                    <button
                      type="button"
                      className="m-btn-pill m-btn-danger"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        exec && handleDeletar(exec.id);
                      }}
                      disabled={deletandoId === item.id}
                    >
                      {deletandoId === item.id ? "..." : "Deletar"}
                    </button>
                  </div>
                </div>
              </article>
            );
          }}
          carregando={carregando}
          erro={erro}
          titulo="Preventivas finalizadas"
          searchPlaceholder="Buscar preventiva..."
          emptyMessage="Nenhuma preventiva finalizada encontrada."
        />
      </LayoutMobile>
    );
  }

  return (
    <LayoutDesktop
      tema={tema}
      titulo="Preventivas Finalizadas"
      subtitulo="Preventivas / Finalizadas"
    >
      {execucaoSelecionada && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={fecharDetalhes}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
            zIndex: 1000,
          }}
        >
          <div
            className="pc-card"
            onClick={(ev) => ev.stopPropagation()}
            style={{
              width: "min(980px, 96vw)",
              maxHeight: "86vh",
              overflow: "auto",
            }}
          >
            <header
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <h3 style={{ margin: 0 }}>Respostas da preventiva</h3>
                <p style={{ marginTop: 6, opacity: 0.85 }}>
                  {execucaoSelecionada.preventivaCodigo
                    ? `Código: ${execucaoSelecionada.preventivaCodigo} · `
                    : ""}
                  {montarNomeExibicaoPreventiva(execucaoSelecionada) || ""}
                </p>
                <p style={{ marginTop: 6, opacity: 0.85 }}>
                  {execucaoSelecionada.empresaNome
                    ? `Empresa: ${execucaoSelecionada.empresaNome} · `
                    : ""}
                  {execucaoSelecionada.maquinaNome
                    ? `Máquina: ${execucaoSelecionada.maquinaNome} · `
                    : ""}
                  Execução: {formatDataHora(execucaoSelecionada.dataExecucao)}
                </p>
              </div>

              <button
                type="button"
                className="pc-btn-secundario"
                onClick={fecharDetalhes}
                style={{ padding: "8px 12px" }}
              >
                Fechar
              </button>
            </header>

            <div style={{ marginTop: 14 }}>
              <h4 style={{ marginTop: 0 }}>Checklist / Perguntas e respostas</h4>

              {Array.isArray(execucaoSelecionada.respostas) &&
              execucaoSelecionada.respostas.length > 0 ? (
                <div className="pc-table-wrapper" style={{ marginTop: 10 }}>
                  <table className="pc-table">
                    <thead>
                      <tr>
                        <th style={{ width: 80 }}>#</th>
                        <th>Pergunta</th>
                        <th style={{ width: 240 }}>Resposta</th>
                      </tr>
                    </thead>
                    <tbody>
                      {execucaoSelecionada.respostas.map((r, i) => (
                        <tr key={String(r?.index ?? i)}>
                          <td>{(r?.index ?? i) + 1}</td>
                          <td>{r?.pergunta || "-"}</td>
                          <td>{r?.valor ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ opacity: 0.85 }}>
                  Nenhuma resposta registrada nesta execução.
                </p>
              )}

              {execucaoSelecionada.observacoes && (
                <div style={{ marginTop: 14 }}>
                  <h4 style={{ marginBottom: 8 }}>Observações</h4>
                  <div
                    style={{
                      background: "rgba(255,255,255,0.06)",
                      border: "1px solid rgba(255,255,255,0.10)",
                      padding: 12,
                      borderRadius: 10,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {execucaoSelecionada.observacoes}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <section className="pc-card pc-table-card">
        <header className="pc-table-header">
          <div className="pc-table-header-text">
            <h3>Preventivas finalizadas</h3>
            <p>
              Histórico de execuções de preventivas registradas no sistema, da mais recente para a mais antiga.
            </p>
          </div>
        </header>

        <div style={{ marginBottom: 12 }}>
          <input
            type="text"
            value={textoFiltro}
            onChange={(e) => setTextoFiltro(e.target.value)}
            placeholder="Filtrar por código, nome, máquina ou executante..."
            style={{
              width: "100%",
              maxWidth: 420,
              padding: "8px 10px",
              borderRadius: 8,
              border: "1px solid #334155",
              
              color: "inherit",
            }}
          />
        </div>

        <div className="pc-table-wrapper">
          {erro && (
            <div
              style={{
                background: "#7f1d1d",
                color: "#fecaca",
                padding: 8,
                borderRadius: 8,
                marginBottom: 12,
                fontSize: 14,
              }}
            >
              {erro}
            </div>
          )}

          {carregando ? (
            <p style={{ padding: 10 }}>Carregando preventivas finalizadas...</p>
          ) : execucoesFiltradas.length === 0 ? (
            <p style={{ padding: 10 }}>Nenhuma preventiva finalizada encontrada.</p>
          ) : (
            <table className="pc-table">
              <thead>
                <tr>
                  <th>Data Execução</th>
                  <th>Código</th>
                  <th>Nome</th>
                  <th>Empresa</th>
                  <th>Máquina</th>
                  <th>Periodicidade</th>
                  <th>Executante</th>
                  <th style={{ width: 120 }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {execucoesFiltradas.map((e) => (
                  <tr key={e.id}>
                    <td>{formatData(e.dataExecucao)}</td>
                    <td>{e.preventivaCodigo || "-"}</td>
                    <td>{montarNomeExibicaoPreventiva(e) || "-"}</td>
                    <td>{e.empresaNome || "-"}</td>
                    <td>{e.maquinaNome || "-"}</td>
                    <td>{e.periodicidade || "-"}</td>
                    <td>{e.executanteNome || "-"}</td>
                    <td>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          type="button"
                          className="pc-btn-secundario"
                          onClick={() => abrirDetalhes(e)}
                          style={{ padding: "6px 10px" }}
                          title="Ver perguntas e respostas"
                        >
                          Ver
                        </button>
                        <button
                          type="button"
                          className="pc-btn-perigo"
                          onClick={() => handleDeletar(e.id)}
                          disabled={deletandoId === e.id}
                          style={{ padding: "6px 10px" }}
                          title="Excluir execução"
                        >
                          {deletandoId === e.id ? "..." : "Deletar"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </LayoutDesktop>
  );
}

export default PreventivasFinalizadasPage;
